import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { UserAvatar } from '@/components/UserAvatar';
import { MobileMenuTrigger } from '@/components/Layout';
import { FeedSkeleton } from '@/components/Skeleton';
import PullToRefresh from '@/components/PullToRefresh';
import { hapticLight, hapticSuccess } from '@/utils/haptics';
import { 
  MessageCircle, Send, Trophy, Lightbulb, HelpCircle, Flame, 
  TrendingUp, Clock, ArrowLeft, X
} from 'lucide-react';
import { toast } from 'sonner';

// Post types with styling
const POST_TYPES = [
  { id: 'win', label: 'Win', icon: Trophy, color: 'text-amber-700', bg: 'bg-amber-100', border: 'border-amber-200' },
  { id: 'learning', label: 'Learning', icon: Lightbulb, color: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-200' },
  { id: 'help', label: 'Help', icon: HelpCircle, color: 'text-purple-700', bg: 'bg-purple-100', border: 'border-purple-200' },
];

// Reaction Types
const REACTION_TYPES = [
  { id: 'fired_up', emoji: '🔥', label: 'Fired Up' },
  { id: 'respect', emoji: '👏', label: 'Respect' },
  { id: 'smart_move', emoji: '💡', label: 'Smart Move' },
];

// Highlight mentions in text
const HighlightedContent = ({ content, truncate = false }) => {
  const parts = content.split(/(@\w+)/g);
  const fullContent = parts.map((part, i) => 
    part.startsWith('@') 
      ? <span key={i} className="text-blue-600 font-medium">{part}</span> 
      : <span key={i}>{part}</span>
  );
  
  if (truncate && content.length > 200) {
    return (
      <span className="line-clamp-3">
        {fullContent}
      </span>
    );
  }
  return <span>{fullContent}</span>;
};

// Get post type config
const getPostTypeConfig = (type) => {
  return POST_TYPES.find(t => t.id === type) || POST_TYPES[0];
};

// Format relative time
const formatTimeAgo = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Extract win amount from post content
const extractWinAmount = (content) => {
  const match = content.match(/₹[\d.,]+\s*(Cr|L|Lakh|Crore)/i);
  return match ? match[0] : null;
};

// Extract project name from post content  
const extractProjectName = (content) => {
  const match = content.match(/at\s+([A-Z][a-zA-Z\s]+?)(?:\.|,|$|\n)/);
  return match ? match[1].trim() : null;
};

// ============================================
// POST CARD COMPONENT (Twitter-style)
// ============================================
const PostCard = ({ post, user, onOpenThread, onReact }) => {
  const typeConfig = getPostTypeConfig(post.post_type);
  const TypeIcon = typeConfig.icon;
  const [animatingReaction, setAnimatingReaction] = useState(null);
  
  // Count reactions
  const reactionCounts = {};
  REACTION_TYPES.forEach(r => {
    reactionCounts[r.id] = (post.reactions?.[r.id] || []).length;
  });
  const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + b, 0);
  const commentCount = post.comments?.length || 0;
  
  // Win highlight
  const winAmount = post.post_type === 'win' ? extractWinAmount(post.content) : null;
  const projectName = post.post_type === 'win' ? extractProjectName(post.content) : null;
  
  // First comment preview
  const firstComment = post.comments?.[0];
  
  // Handle reaction with animation
  const handleReact = (e, reactionId) => {
    e.stopPropagation();
    setAnimatingReaction(reactionId);
    onReact(post.post_id, reactionId);
    setTimeout(() => setAnimatingReaction(null), 300);
  };

  return (
    <div 
      onClick={() => onOpenThread(post)}
      className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer active:scale-[0.99] border border-slate-100"
      data-testid={`post-card-${post.post_id}`}
    >
      {/* Row 1: Avatar, Name, Tag, Timestamp */}
      <div className="flex items-center gap-3 mb-2">
        <UserAvatar 
          name={post.author_name} 
          imageUrl={post.author_avatar} 
          size="sm" 
        />
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-sm text-slate-800 truncate block">
            {post.author_name}
          </span>
        </div>
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeConfig.bg} ${typeConfig.color}`}>
          <TypeIcon size={10} />
          {typeConfig.label}
        </div>
        <span className="text-xs text-slate-400 whitespace-nowrap">
          {formatTimeAgo(post.created_at)}
        </span>
      </div>
      
      {/* Row 2: Post Content (max 3 lines) */}
      <div className="text-sm text-slate-700 mb-2 leading-relaxed">
        <HighlightedContent content={post.content} truncate={true} />
        {post.content.length > 200 && (
          <span className="text-blue-600 font-medium ml-1">See more…</span>
        )}
      </div>
      
      {/* Row 3: Win Highlight Strip (only for wins with amount) */}
      {post.post_type === 'win' && (winAmount || projectName) && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3">
          <p className="text-xs font-medium text-amber-800">
            {winAmount && <span>Closed {winAmount}</span>}
            {winAmount && projectName && <span> at </span>}
            {projectName && <span className="font-semibold">{projectName}</span>}
          </p>
        </div>
      )}
      
      {/* Row 4: Reactions Summary */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <div className="flex items-center gap-3">
          {REACTION_TYPES.map(reaction => {
            const count = reactionCounts[reaction.id];
            const isReacted = post.reactions?.[reaction.id]?.includes(user?.user_id);
            if (count === 0 && !isReacted) return null;
            
            return (
              <button
                key={reaction.id}
                onClick={(e) => handleReact(e, reaction.id)}
                className={`flex items-center gap-1 text-xs transition-all duration-200 ${
                  animatingReaction === reaction.id ? 'scale-125' : ''
                } ${isReacted ? 'font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <span className={animatingReaction === reaction.id ? 'animate-bounce' : ''}>
                  {reaction.emoji}
                </span>
                <span>{count || ''}</span>
              </button>
            );
          })}
          
          {/* Add reaction buttons for empty reactions */}
          {totalReactions === 0 && (
            <div className="flex items-center gap-2">
              {REACTION_TYPES.map(reaction => (
                <button
                  key={reaction.id}
                  onClick={(e) => handleReact(e, reaction.id)}
                  className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-slate-100 rounded-full"
                >
                  <span className="text-sm">{reaction.emoji}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Comment Count */}
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <MessageCircle size={14} />
          <span>{commentCount} {commentCount === 1 ? 'comment' : 'comments'}</span>
        </div>
      </div>
      
      {/* Comment Preview (if has comments) */}
      {firstComment && (
        <div className="mt-2 pt-2 border-t border-slate-50">
          <p className="text-xs text-slate-600 truncate">
            <span className="font-semibold">{firstComment.author_name?.split(' ')[0]}:</span>{' '}
            {firstComment.content}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// THREAD VIEW COMPONENT (Full Screen)
// ============================================
const ThreadView = ({ post, user, onClose, onReact, onComment }) => {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const commentsEndRef = useRef(null);
  
  const typeConfig = getPostTypeConfig(post.post_type);
  const TypeIcon = typeConfig.icon;
  
  // Count reactions
  const reactionCounts = {};
  REACTION_TYPES.forEach(r => {
    reactionCounts[r.id] = (post.reactions?.[r.id] || []).length;
  });
  const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + b, 0);
  
  // Win highlight
  const winAmount = post.post_type === 'win' ? extractWinAmount(post.content) : null;
  const projectName = post.post_type === 'win' ? extractProjectName(post.content) : null;
  
  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await onComment(post.post_id, newComment);
      setNewComment('');
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col animate-in slide-in-from-right duration-200">
      {/* Top Bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white sticky top-0 z-10">
        <button 
          onClick={onClose}
          className="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft size={20} className="text-slate-600" />
        </button>
        <UserAvatar name={post.author_name} imageUrl={post.author_avatar} size="sm" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-sm text-slate-800 truncate block">
            {post.author_name}
          </span>
        </div>
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeConfig.bg} ${typeConfig.color}`}>
          <TypeIcon size={10} />
          {typeConfig.label}
        </div>
        <span className="text-xs text-slate-400">
          {formatTimeAgo(post.created_at)}
        </span>
      </div>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          {/* Full Post Content */}
          <div className="text-base text-slate-800 leading-relaxed mb-4">
            <HighlightedContent content={post.content} />
          </div>
          
          {/* Win Highlight Strip */}
          {post.post_type === 'win' && (winAmount || projectName) && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4">
              <p className="text-sm font-medium text-amber-800">
                {winAmount && <span>Closed {winAmount}</span>}
                {winAmount && projectName && <span> at </span>}
                {projectName && <span className="font-semibold">{projectName}</span>}
              </p>
            </div>
          )}
          
          {/* Engagement Bar */}
          <div className="flex items-center justify-between py-3 border-y border-slate-100 mb-4">
            <div className="flex items-center gap-4">
              {REACTION_TYPES.map(reaction => {
                const count = reactionCounts[reaction.id];
                const isReacted = post.reactions?.[reaction.id]?.includes(user?.user_id);
                
                return (
                  <button
                    key={reaction.id}
                    onClick={() => onReact(post.post_id, reaction.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-200 ${
                      isReacted 
                        ? 'bg-slate-100 font-semibold' 
                        : 'hover:bg-slate-50 text-slate-600'
                    }`}
                  >
                    <span>{reaction.emoji}</span>
                    <span className="text-sm">{count || 0}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-slate-500">
              <MessageCircle size={16} />
              <span>{post.comments?.length || 0}</span>
            </div>
          </div>
          
          {/* Comments Thread */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">
              Comments ({post.comments?.length || 0})
            </h3>
            
            {post.comments?.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">
                Be the first to comment
              </p>
            ) : (
              post.comments?.map((comment, idx) => (
                <div key={comment.comment_id || idx} className="flex gap-3">
                  <UserAvatar 
                    name={comment.author_name} 
                    imageUrl={comment.author_avatar} 
                    size="sm" 
                  />
                  <div className="flex-1 bg-slate-50 rounded-xl px-3 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-slate-800">
                        {comment.author_name}
                      </span>
                      <span className="text-xs text-slate-400">
                        {formatTimeAgo(comment.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">
                      <HighlightedContent content={comment.content} />
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={commentsEndRef} />
          </div>
        </div>
      </div>
      
      {/* Sticky Comment Input */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 safe-bottom">
        <div className="flex gap-2">
          <Input
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmitComment()}
            placeholder="Write a comment…"
            className="flex-1 rounded-full bg-slate-100 border-0"
          />
          <Button
            onClick={handleSubmitComment}
            disabled={!newComment.trim() || submitting}
            className="rounded-full bg-[#5B3DF5] hover:bg-[#4A2FE0] px-4"
          >
            <Send size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN FEED COMPONENT
// ============================================
const Feed = ({ user }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [postType, setPostType] = useState('win');
  const [loading, setLoading] = useState(false);
  const [sortMode, setSortMode] = useState('latest'); // 'trending' or 'latest'
  const [selectedPost, setSelectedPost] = useState(null);
  const [myEngagement, setMyEngagement] = useState({ 
    weekly_points: 0, 
    rank: 0,
    gap_to_first: 0
  });
  const [topContributors, setTopContributors] = useState([]);

  useEffect(() => {
    fetchPosts();
    fetchTopContributors();
    fetchMyEngagement();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.getPosts();
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    }
  };

  const fetchTopContributors = async () => {
    try {
      const response = await api.getTopContributors();
      setTopContributors(response.data);
    } catch (error) {
      console.error('Failed to load top contributors');
    }
  };

  const fetchMyEngagement = async () => {
    try {
      const response = await api.getMyEngagement();
      setMyEngagement(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load engagement');
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim()) {
      toast.error('Post cannot be empty');
      return;
    }
    setLoading(true);
    try {
      const response = await api.createPost({ content: newPost, post_type: postType });
      const pointsAwarded = response.data.points_awarded || 0;
      setNewPost('');
      setPostType('win');
      if (pointsAwarded > 0) {
        toast.success(`🔥 Post created! +${pointsAwarded} pts`);
      } else {
        toast.success('Post created!');
      }
      fetchPosts();
      fetchMyEngagement();
      fetchTopContributors();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleReact = async (postId, reactionType) => {
    try {
      await api.reactToPost(postId, reactionType);
      // Refresh post data
      const response = await api.getPosts();
      setPosts(response.data);
      // Update selected post if viewing thread
      if (selectedPost?.post_id === postId) {
        const updatedPost = response.data.find(p => p.post_id === postId);
        if (updatedPost) setSelectedPost(updatedPost);
      }
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const handleComment = async (postId, content) => {
    try {
      await api.createComment(postId, { content });
      // Refresh posts
      const response = await api.getPosts();
      setPosts(response.data);
      // Update selected post
      const updatedPost = response.data.find(p => p.post_id === postId);
      if (updatedPost) setSelectedPost(updatedPost);
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const openThread = (post) => {
    setSelectedPost(post);
  };

  const closeThread = () => {
    setSelectedPost(null);
  };

  // Sort posts based on mode
  const sortedPosts = [...posts].sort((a, b) => {
    if (sortMode === 'trending') {
      // Weighted score: reactions + comments * 2
      const scoreA = Object.values(a.reactions || {}).flat().length + (a.comments?.length || 0) * 2;
      const scoreB = Object.values(b.reactions || {}).flat().length + (b.comments?.length || 0) * 2;
      return scoreB - scoreA;
    }
    // Latest (chronological)
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Filter out system posts for cleaner feed
  const displayedPosts = sortedPosts.filter(p => !p.is_system_post);

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    try {
      await Promise.all([fetchPosts(), fetchTopContributors(), fetchMyEngagement()]);
      hapticSuccess();
      toast.success('Feed refreshed');
    } catch (error) {
      toast.error('Refresh failed');
    }
  }, []);

  return (
    <div className="h-[100dvh] bg-[#F7F8FA] page-fade-in">
      {/* Header - Standardized ← Title format */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shadow-sm header-blur sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="page-back-btn"
            >
              <ArrowLeft size={20} className="text-slate-700" strokeWidth={2.5} />
            </button>
            <h1 className="text-lg font-bold text-[#111827]">Feed</h1>
          </div>
          <MobileMenuTrigger user={user} />
        </div>
      </div>

      <PullToRefresh onRefresh={handleRefresh}>
        <div className="px-4 py-3 max-w-2xl mx-auto space-y-3 pb-20">
        {/* Compact This Week's Race Widget */}
        <Card className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-100 rounded-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <Flame size={20} className="text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">This Week's Race</p>
                <p className="text-lg font-bold text-slate-800">{myEngagement.weekly_points} pts</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Rank</p>
              <p className="text-lg font-bold text-purple-600">#{myEngagement.rank || '-'}</p>
            </div>
          </div>
          
          {/* Top 3 Contributors Mini */}
          {topContributors.length > 0 && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-purple-100">
              <span className="text-xs text-slate-500">Leaders:</span>
              <div className="flex -space-x-2">
                {topContributors.slice(0, 3).map((c, i) => (
                  <div key={c.user_id} className="relative">
                    <UserAvatar name={c.name} imageUrl={c.avatar} size="xs" />
                    {i === 0 && (
                      <span className="absolute -top-1 -right-1 text-[8px]">👑</span>
                    )}
                  </div>
                ))}
              </div>
              <span className="text-xs font-medium text-slate-600 ml-1">
                {topContributors[0]?.name?.split(' ')[0]} leads with {topContributors[0]?.total_points}pts
              </span>
            </div>
          )}
        </Card>

        {/* Post Input Box - Compact */}
        <Card className="p-3 bg-white rounded-2xl shadow-sm">
          <div className="flex gap-2">
            <UserAvatar 
              name={user?.rep_name || user?.email} 
              imageUrl={user?.profile_image_url} 
              size="sm" 
            />
            <div className="flex-1 space-y-2">
              {/* Post Type Pills */}
              <div className="flex gap-1.5">
                {POST_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setPostType(type.id)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 ${
                        postType === type.id 
                          ? `${type.bg} ${type.color} shadow-sm` 
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      <Icon size={11} />
                      {type.label}
                    </button>
                  );
                })}
              </div>
              
              <Textarea
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                placeholder={
                  postType === 'win' ? "Share your win!" 
                  : postType === 'learning' ? "What did you learn?" 
                  : "Ask for help…"
                }
                className="min-h-[60px] resize-none border-slate-200 rounded-xl text-sm py-2"
              />
              
              <Button 
                onClick={handleCreatePost} 
                disabled={loading || !newPost.trim()} 
                className="w-full bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl h-9 font-semibold text-sm"
              >
                <Send size={13} className="mr-1.5" />
                {loading ? 'Posting...' : 'Post'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Sort Toggle */}
        <div className="flex items-center gap-2 bg-white rounded-full p-1 shadow-sm w-fit">
          <button
            onClick={() => setSortMode('trending')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              sortMode === 'trending' 
                ? 'bg-[#5B3DF5] text-white shadow-sm' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Flame size={14} />
            Trending
          </button>
          <button
            onClick={() => setSortMode('latest')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              sortMode === 'latest' 
                ? 'bg-[#5B3DF5] text-white shadow-sm' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Clock size={14} />
            Latest
          </button>
        </div>

        {/* Posts List */}
        {displayedPosts.length === 0 ? (
          <Card className="p-8 text-center bg-white rounded-2xl shadow-sm">
            <p className="text-slate-500">No posts yet. Be the first to share!</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {displayedPosts.map((post) => (
              <PostCard
                key={post.post_id}
                post={post}
                user={user}
                onOpenThread={openThread}
                onReact={handleReact}
              />
            ))}
          </div>
        )}
      </div>
      </PullToRefresh>

      {/* Thread View Modal */}
      {selectedPost && (
        <ThreadView
          post={selectedPost}
          user={user}
          onClose={closeThread}
          onReact={handleReact}
          onComment={handleComment}
        />
      )}
    </div>
  );
};

export default Feed;
